import { Activity, AlertTriangle, ClipboardCheck, FileText, RotateCcw, Search, Warehouse } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PdfFileActions } from "@/components/PdfFileActions";

const formatMoney = (value) => `${Number(value || 0).toLocaleString("fr-FR")} GNF`;
const timelineLabel = {
  entree: "Entrée",
  sortie: "Sortie",
  transfert: "Transfert",
  initial: "Stock initial",
};

export const WarehouseDetailSheet = ({
  defaultTab = "overview",
  onMovementOpen,
  onOpenChange,
  user,
  warehouseId,
}) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [stockSearch, setStockSearch] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyType, setHistoryType] = useState("");
  const [historyPeriod, setHistoryPeriod] = useState("all");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [creatingAudit, setCreatingAudit] = useState(false);
  const [latestCancellableMovement, setLatestCancellableMovement] = useState(null);
  const [isCancellationConfirming, setIsCancellationConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const load = () => {
    if (!warehouseId) {
      setDetail(null);
      setError("");
      return Promise.resolve();
    }

    return api.get(`/warehouses/${warehouseId}/details`)
      .then((response) => {
        setDetail(response.data);
        setError("");
      })
      .catch((requestError) => setError(getError(requestError)));
  };

  useEffect(() => {
    load();
  }, [warehouseId]);

  const warehouse = detail?.warehouse;
  const filteredStock = useMemo(() => {
    const normalizedSearch = stockSearch.toLocaleLowerCase("fr-FR");

    return (detail?.stock || []).filter((row) => {
      const matchesSearch = row.product_name.toLocaleLowerCase("fr-FR").includes(normalizedSearch);
      const matchesStatus = !stockStatus
        || (stockStatus === "alert" && row.is_alert)
        || (stockStatus === "normal" && !row.is_alert);

      return matchesSearch && matchesStatus;
    });
  }, [detail?.stock, stockSearch, stockStatus]);
  const filteredMovements = useMemo(() => {
    const normalizedSearch = historySearch.toLocaleLowerCase("fr-FR");
    const today = new Date();

    return (detail?.movements || []).filter((movement) => {
      const matchesType = !historyType || movement.movement_type === historyType;
      const searchText = [
        movement.movement_id,
        movement.source_name,
        movement.destination_name,
        movement.author_name,
        movement.notes,
        ...movement.lines.map((line) => line.product_name),
      ].join(" ").toLocaleLowerCase("fr-FR");
      const movementDate = new Date(movement.movement_date || movement.created_at);
      const ageInDays = Math.floor((today - movementDate) / 86400000);
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
      weekStart.setHours(0, 0, 0, 0);
      const customStart = historyDateFrom ? new Date(`${historyDateFrom}T00:00:00`) : null;
      const customEnd = historyDateTo ? new Date(`${historyDateTo}T23:59:59`) : null;
      const matchesPeriod = historyPeriod === "all"
        || (historyPeriod === "today" && ageInDays === 0)
        || (historyPeriod === "yesterday" && ageInDays === 1)
        || (historyPeriod === "this_week" && movementDate >= weekStart)
        || (historyPeriod === "last_7_days" && ageInDays <= 6)
        || (historyPeriod === "this_month"
          && movementDate.getMonth() === today.getMonth()
          && movementDate.getFullYear() === today.getFullYear())
        || (historyPeriod === "last_30_days" && ageInDays <= 29)
        || (historyPeriod === "last_90_days" && ageInDays <= 89)
        || (historyPeriod === "this_year" && movementDate.getFullYear() === today.getFullYear())
        || (historyPeriod === "custom"
          && (!customStart || movementDate >= customStart)
          && (!customEnd || movementDate <= customEnd));

      return matchesType && searchText.includes(normalizedSearch) && matchesPeriod;
    });
  }, [detail?.movements, historyDateFrom, historyDateTo, historyPeriod, historySearch, historyType]);
  const stockValue = filteredStock.reduce(
    (sum, row) => sum + Number(row.quantity || 0) * Number(row.sale_price || 0),
    0,
  );
  const chronologicalMovements = useMemo(() => (
    [...(detail?.movements || [])]
      .sort((first, second) => (
        new Date(first.movement_date || first.created_at)
          - new Date(second.movement_date || second.created_at)
      ))
      .slice(-12)
  ), [detail?.movements]);

  const getStockPdfBlob = async () => {
    const response = await api.get(`/reports/warehouse/${warehouseId}/pdf`, {
      params: { search: stockSearch, stock_status: stockStatus },
      responseType: "blob",
    });
    return response.data;
  };
  const getHistoryPdfBlob = async () => {
    const response = await api.get(`/warehouses/${warehouseId}/history/pdf`, {
      params: {
        search: historySearch,
        movement_type: historyType,
        period: historyPeriod,
        date_from: historyDateFrom,
        date_to: historyDateTo,
      },
      responseType: "blob",
    });
    return response.data;
  };
  const createAudit = async () => {
    setCreatingAudit(true);

    try {
      await api.post(`/warehouses/${warehouseId}/audit`);
      toast.success("Audit du dépôt enregistré.");
      await load();
    } catch (requestError) {
      toast.error(getError(requestError));
    } finally {
      setCreatingAudit(false);
    }
  };
  const openMovement = (movementId) => {
    onOpenChange(false);
    onMovementOpen(movementId);
  };
  const canCancelLastMovement = ["admin_principal", "admin_delegue"].includes(user?.role);

  useEffect(() => {
    if (!canCancelLastMovement || !warehouseId) {
      setLatestCancellableMovement(null);
      return;
    }

    api.get("/movements/latest", { params: { warehouse_id: warehouseId } })
      .then((response) => setLatestCancellableMovement(response.data.movement))
      .catch(() => setLatestCancellableMovement(null));
  }, [canCancelLastMovement, detail?.movements, warehouseId]);

  const cancelLastMovement = async () => {
    if (!latestCancellableMovement) {
      return;
    }
    setIsCancelling(true);
    try {
      await api.post(`/movements/${latestCancellableMovement.movement_id}/cancel`, {
        warehouse_id: warehouseId,
        confirmation: "ANNULER",
      });
      toast.success("Le dernier mouvement a été annulé et le stock a été restauré.");
      setLatestCancellableMovement(null);
      setIsCancellationConfirming(false);
      await load();
    } catch (requestError) {
      toast.error(getError(requestError));
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={Boolean(warehouseId)}>
      <SheetContent className="detail-sheet-content detail-sheet-wide" data-testid="warehouse-detail-sheet" side="right">
        {!warehouse && <SheetTitle className="sr-only">Détails du dépôt</SheetTitle>}
        {!warehouse && <SheetDescription className="sr-only">Chargement des détails du dépôt</SheetDescription>}
        {error && <p className="detail-sheet-error" data-testid="warehouse-detail-error">{error}</p>}
        {!warehouse && !error && <div className="loading-line" data-testid="warehouse-detail-loading">Chargement…</div>}
        {warehouse && (
          <>
            <SheetHeader className="detail-sheet-header">
              <div>
                <p className="eyebrow">DÉPÔT · {warehouse.warehouse_id}</p>
                <SheetTitle>{warehouse.name}</SheetTitle>
                <SheetDescription>{warehouse.subzone} · {warehouse.warehouse_type}</SheetDescription>
              </div>
              <PdfFileActions
                fileName={`stock-${warehouse.warehouse_id}.pdf`}
                getPdfBlob={getStockPdfBlob}
                testIdPrefix="warehouse-stock"
              />
            </SheetHeader>
            <div className="detail-sheet-scroll">
              <Tabs
                defaultValue={defaultTab}
                key={`${warehouseId}-${defaultTab}`}
                data-testid="warehouse-detail-tabs"
              >
                <TabsList className="warehouse-tabs-list">
                  <TabsTrigger data-testid="warehouse-tab-overview" value="overview">Vue générale</TabsTrigger>
                  <TabsTrigger data-testid="warehouse-tab-stock" value="stock">Stock</TabsTrigger>
                  <TabsTrigger data-testid="warehouse-tab-history" value="history">Historique</TabsTrigger>
                  <TabsTrigger data-testid="warehouse-tab-audit" value="audit">Audit</TabsTrigger>
                </TabsList>
                <TabsContent value="overview">
                  <section className="detail-metadata-grid" data-testid="warehouse-overview-metrics">
                    <article><Warehouse size={17} /><span>Produits en stock</span><strong>{detail.stock.length}</strong></article>
                    <article><FileText size={17} /><span>Valeur disponible</span><strong>{formatMoney(stockValue)}</strong></article>
                    <article><Search size={17} /><span>Mouvements</span><strong>{detail.movements.length}</strong></article>
                    <article><ClipboardCheck size={17} /><span>Audits enregistrés</span><strong>{detail.audits.length}</strong></article>
                  </section>
                  <section className="detail-section" data-testid="warehouse-address-detail">
                    <p className="eyebrow">EMPLACEMENT</p>
                    <strong>{warehouse.zone_id} · {warehouse.subzone}</strong>
                    <p>{warehouse.active ? "Dépôt actif" : "Dépôt inactif"}</p>
                  </section>
                  <section className="warehouse-movement-timeline" data-testid="warehouse-movement-timeline">
                    <div className="warehouse-movement-timeline-heading">
                      <div>
                        <p className="eyebrow">FRISE OPÉRATIONNELLE</p>
                        <h3>Derniers mouvements du dépôt</h3>
                      </div>
                      <Activity aria-hidden="true" size={20} />
                    </div>
                    {!chronologicalMovements.length && (
                      <p data-testid="warehouse-movement-timeline-empty">
                        Aucun mouvement n’est encore enregistré pour ce dépôt.
                      </p>
                    )}
                    <div className="warehouse-movement-timeline-list">
                      {chronologicalMovements.map((movement) => {
                        const quantity = movement.lines.reduce(
                          (sum, line) => sum + Number(line.quantity || 0),
                          0,
                        );
                        const isCancelled = movement.status === "annule";

                        return (
                          <button
                            className={`warehouse-timeline-item ${movement.movement_type} ${isCancelled ? "cancelled" : ""}`}
                            data-testid={`warehouse-timeline-movement-${movement.movement_id}`}
                            key={movement.movement_id}
                            onClick={() => openMovement(movement.movement_id)}
                            type="button"
                          >
                            <time>{new Date(movement.movement_date || movement.created_at).toLocaleDateString("fr-FR")}</time>
                            <span className="warehouse-timeline-marker" aria-hidden="true" />
                            <span className="warehouse-timeline-content">
                              <strong>
                                {isCancelled ? "Annulé" : timelineLabel[movement.movement_type]}
                                <b>{quantity} unité(s)</b>
                              </strong>
                              <small>
                                {movement.source_name}
                                {movement.destination_name ? ` → ${movement.destination_name}` : ""}
                              </small>
                              <em>{movement.lines.map((line) => line.product_name).join(", ")}</em>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </TabsContent>
                <TabsContent value="stock">
                  <section className="detail-section">
                    <div className="detail-section-heading">
                      <div><p className="eyebrow">STOCK DISPONIBLE</p><h3>{filteredStock.length} produit(s)</h3></div>
                      <input
                        data-testid="warehouse-stock-search-input"
                        onChange={(event) => setStockSearch(event.target.value)}
                        placeholder="Rechercher un produit"
                        value={stockSearch}
                      />
                      <select
                        data-testid="warehouse-stock-status-filter"
                        onChange={(event) => setStockStatus(event.target.value)}
                        value={stockStatus}
                      >
                        <option value="">Tous les statuts</option>
                        <option value="alert">Sous seuil</option>
                        <option value="normal">Stock normal</option>
                      </select>
                    </div>
                    <div className="warehouse-stock-list" data-testid="warehouse-stock-list">
                      {filteredStock.map((row) => (
                        <div data-testid={`warehouse-stock-${row.inventory_id}`} key={row.inventory_id}>
                          <span><strong>{row.product_name}</strong><small>Seuil : {row.threshold}</small></span>
                          <b>{row.quantity}</b>
                          <em>{formatMoney(Number(row.quantity) * Number(row.sale_price))}</em>
                        </div>
                      ))}
                    </div>
                  </section>
                </TabsContent>
                <TabsContent value="history">
                  <section className="detail-section">
                    <div className="detail-section-heading detail-history-heading">
                      <div><p className="eyebrow">HISTORIQUE FILTRABLE</p><h3>{filteredMovements.length} mouvement(s)</h3></div>
                      <PdfFileActions
                        fileName={`historique-${warehouse.warehouse_id}.pdf`}
                        getPdfBlob={getHistoryPdfBlob}
                        testIdPrefix="warehouse-history"
                      />
                    </div>
                    <div className="detail-history-filters" data-testid="warehouse-history-filters">
                      <input
                        data-testid="warehouse-history-search-input"
                        onChange={(event) => setHistorySearch(event.target.value)}
                        placeholder="N°, produit, auteur, dépôt…"
                        value={historySearch}
                      />
                      <select
                        data-testid="warehouse-history-type-filter"
                        onChange={(event) => setHistoryType(event.target.value)}
                        value={historyType}
                      >
                        <option value="">Tous les types</option>
                        <option value="entree">Entrée</option>
                        <option value="sortie">Sortie</option>
                        <option value="transfert">Transfert</option>
                        <option value="initial">Stock initial</option>
                      </select>
                      <select
                        data-testid="warehouse-history-period-filter"
                        onChange={(event) => setHistoryPeriod(event.target.value)}
                        value={historyPeriod}
                      >
                        <option value="all">Toute la période</option>
                        <option value="today">Aujourd’hui</option>
                        <option value="yesterday">Hier</option>
                        <option value="this_week">Cette semaine</option>
                        <option value="last_7_days">7 derniers jours</option>
                        <option value="this_month">Ce mois-ci</option>
                        <option value="last_30_days">30 derniers jours</option>
                        <option value="last_90_days">90 derniers jours</option>
                        <option value="this_year">Cette année</option>
                        <option value="custom">Période personnalisée</option>
                      </select>
                      {historyPeriod === "custom" && (
                        <>
                          <input
                            data-testid="warehouse-history-date-from-input"
                            onChange={(event) => setHistoryDateFrom(event.target.value)}
                            type="date"
                            value={historyDateFrom}
                          />
                          <input
                            data-testid="warehouse-history-date-to-input"
                            onChange={(event) => setHistoryDateTo(event.target.value)}
                            type="date"
                            value={historyDateTo}
                          />
                        </>
                      )}
                    </div>
                    <div className="detail-history-list" data-testid="warehouse-history-list">
                      {filteredMovements.map((movement) => (
                        <div
                          className={movement.status === "annule" ? "cancelled-history-item" : ""}
                          data-testid={`warehouse-history-item-${movement.movement_id}`}
                          key={movement.movement_id}
                        >
                          <button
                            className="warehouse-history-open-button"
                            data-testid={`warehouse-history-movement-${movement.movement_id}`}
                            onClick={() => openMovement(movement.movement_id)}
                            type="button"
                          >
                            <span><strong>{movement.source_name}</strong><small>{movement.lines.length} produit(s) · {movement.author_name}</small></span>
                            <b>{movement.status === "annule" ? "Annulé" : movement.movement_type}</b>
                            <time>{new Date(movement.movement_date || movement.created_at).toLocaleDateString("fr-FR")}</time>
                          </button>
                          {canCancelLastMovement
                            && latestCancellableMovement?.movement_id === movement.movement_id && (
                              <button
                                className="warehouse-history-cancel-button"
                                data-testid={`warehouse-history-cancel-${movement.movement_id}`}
                                disabled={!latestCancellableMovement.can_execute}
                                onClick={() => setIsCancellationConfirming(true)}
                                type="button"
                              >
                                <RotateCcw size={14} /> Annuler
                              </button>
                            )}
                        </div>
                      ))}
                    </div>
                    {canCancelLastMovement && latestCancellableMovement && (
                      <section
                        className="warehouse-history-cancellation"
                        data-testid="warehouse-history-cancellation-summary"
                      >
                        <div>
                          <p className="eyebrow">DERNIER MOUVEMENT ANNULABLE</p>
                          <strong>{latestCancellableMovement.movement_type}</strong>
                        </div>
                        {latestCancellableMovement.stock_preview.map((line) => (
                          <article key={line.product_id}>
                            <strong>{line.product_name} · {line.movement_quantity}</strong>
                            {line.stock_effects.map((effect) => (
                              <span key={effect.warehouse_id}>
                                {effect.warehouse_name} : {effect.current_quantity} → {effect.quantity_after_cancellation}
                              </span>
                            ))}
                          </article>
                        ))}
                        {!latestCancellableMovement.can_execute && (
                          <p className="warehouse-history-cancel-warning">
                            Les quantités actuelles ne permettent plus une annulation sûre.
                          </p>
                        )}
                        {isCancellationConfirming && (
                          <div
                            className="warehouse-history-cancel-confirmation"
                            data-testid="warehouse-history-cancel-confirmation"
                          >
                            <strong>Confirmer l’annulation définitive</strong>
                            <p>
                              Le stock sera restauré, le document supprimé et une trace restera dans
                              l’historique.
                            </p>
                            <div>
                              <button
                                data-testid="close-warehouse-history-cancel-confirmation-button"
                                onClick={() => setIsCancellationConfirming(false)}
                                type="button"
                              >
                                Retour
                              </button>
                              <button
                                className="destructive-action"
                                data-testid="confirm-warehouse-history-cancel-button"
                                disabled={isCancelling}
                                onClick={cancelLastMovement}
                                type="button"
                              >
                                <AlertTriangle size={15} />
                                {isCancelling ? "Annulation…" : "Annuler définitivement"}
                              </button>
                            </div>
                          </div>
                        )}
                      </section>
                    )}
                  </section>
                </TabsContent>
                <TabsContent value="audit">
                  <section className="detail-section">
                    <div className="detail-section-heading">
                      <div><p className="eyebrow">AUDIT DU DÉPÔT</p><h3>Instantanés de stock</h3></div>
                      <button
                        className="secondary-action"
                        data-testid="create-warehouse-audit-button"
                        disabled={creatingAudit}
                        onClick={createAudit}
                        type="button"
                      >
                        <ClipboardCheck size={16} /> {creatingAudit ? "Audit…" : "Créer un audit"}
                      </button>
                    </div>
                    <div className="warehouse-audit-list" data-testid="warehouse-audit-list">
                      {detail.audits.map((audit) => (
                        <div data-testid={`warehouse-audit-${audit.audit_id}`} key={audit.audit_id}>
                          <strong>{audit.item_count} ligne(s) contrôlée(s)</strong>
                          <span>{audit.author_name} · {new Date(audit.created_at).toLocaleString("fr-FR")}</span>
                        </div>
                      ))}
                      {!detail.audits.length && <p>Aucun audit enregistré pour ce dépôt.</p>}
                    </div>
                  </section>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};